import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, FlaskConical } from "lucide-react";
import { api } from "@/api/client";
import type { ApplyTopicResultWithEstimate, ResolvedTopicWithEstimate, TopicEstimate, TopicStudyRef } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { ModelSelector } from "@/components/compare/ModelSelector";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { LensPill } from "@/components/topics/shared";
import { formatTimesPerDay } from "@/lib/format";
import { cn } from "@/lib/utils";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
type Cadence = "weekly" | "daily" | "monthly" | "times_per_day";
const CADENCE_OPTIONS: { value: Cadence; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "times_per_day", label: "Several times a day" },
];

interface TopicTrackingTabProps {
  topicId: number;
  resolved: ResolvedTopicWithEstimate;
  onMutated: () => Promise<void>;
}

export function TopicTrackingTab({ topicId, resolved, onMutated }: TopicTrackingTabProps) {
  const isPulse = resolved.topic.kind === "pulse";
  const tr = resolved.tracking;
  const fetchModels = useModelsStore((s) => s.fetch);

  const [enabled, setEnabled] = useState(!!tr?.enabled);
  const [fieldEnabled, setFieldEnabled] = useState(tr?.field_enabled ?? true);
  const [blindEnabled, setBlindEnabled] = useState(isPulse ? false : (tr?.blind_enabled ?? false));
  const [cadence, setCadence] = useState<Cadence>((tr?.cadence as Cadence) || "weekly");
  const [hourUtc, setHourUtc] = useState(tr?.hour_utc ?? 9);
  const [dayOfWeek, setDayOfWeek] = useState(tr?.day_of_week ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(tr?.day_of_month ?? 1);
  const [timesPerDay, setTimesPerDay] = useState(tr?.times_per_day ?? 4);
  const [models, setModels] = useState<Set<string>>(new Set(tr?.models ?? []));

  const [estimate, setEstimate] = useState<TopicEstimate | null>(resolved.estimate);
  const [applied, setApplied] = useState<ApplyTopicResultWithEstimate | null>(null);
  const [bulkAdopted, setBulkAdopted] = useState<ApplyTopicResultWithEstimate | null>(null);
  const [adoptReview, setAdoptReview] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { fetchModels(); }, [fetchModels]);
  useDefaultModelSeed(models.size === 0, (ids) => setModels(new Set(ids)));

  const fieldPromptCount = resolved.prompts.filter((p) => p.mode === "field" || p.mode === "both").length;
  const blindPromptCount = resolved.prompts.filter((p) => p.mode === "blind" || p.mode === "both").length;

  // Live cost preview — decoration, so failures are silent. Debounced ~400ms.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      api.topics.estimate(topicId, {
        models: [...models],
        cadence,
        times_per_day: cadence === "times_per_day" ? timesPerDay : undefined,
        field_enabled: fieldEnabled,
        blind_enabled: blindEnabled,
      })
        .then((e) => { if (!cancelled) setEstimate(e); })
        .catch(() => {});
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [topicId, models, cadence, timesPerDay, fieldEnabled, blindEnabled]);


  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const result = await api.topics.setTracking(topicId, {
        enabled,
        field_enabled: fieldEnabled,
        blind_enabled: isPulse ? false : blindEnabled,
        cadence,
        hour_utc: hourUtc,
        day_of_week: dayOfWeek,
        day_of_month: dayOfMonth,
        times_per_day: cadence === "times_per_day" ? timesPerDay : null,
        models: [...models],
      });
      setApplied(result);
      setEstimate(result.estimate);
      await onMutated();
    } catch (e: any) {
      setError(e?.message || "Could not save tracking.");
    } finally {
      setSaving(false);
    }
  };

  // Bulk-adopt every scheduled study on the topic. On an untracked topic that
  // only links + seeds — the legacy schedules keep running until competitors
  // are reviewed and tracking is enabled — so the result is a review card, not
  // an "applied" panel. Both banners live here (not in the adoptable card) so
  // they survive the card unmounting when the studies are folded in.
  const adoptAll = async () => {
    const { adopted, apply } = await api.topics.adoptAllStudies(topicId);
    if (apply) {
      setBulkAdopted(apply);
      setEstimate(apply.estimate);
    } else {
      setAdoptReview(adopted);
    }
    await onMutated();
  };

  return (
    <div className="space-y-5">
      {applied && (
        <div className="flex items-center gap-2.5 rounded-lg border border-brand-100 bg-brand-50/50 px-3.5 py-2.5 text-[13px] text-slate-700">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          Created {applied.created.length} · Updated {applied.swapped.length} · Stopped {applied.disabled.length} studies · ~{applied.estimate.estimated_calls_per_month} AI calls/month
        </div>
      )}
      {bulkAdopted && (
        <div className="flex items-center gap-2.5 rounded-lg border border-brand-100 bg-brand-50/50 px-3.5 py-2.5 text-[13px] text-slate-700">
          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
          Adopted studies — kept {bulkAdopted.unchanged.length} · consolidated {bulkAdopted.swapped.length + bulkAdopted.created.length} · stopped {bulkAdopted.disabled.length} studies · ~{bulkAdopted.estimate.estimated_calls_per_month} AI calls/month
        </div>
      )}
      {adoptReview !== null && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-[13.5px] font-bold text-brand-950">
                {adoptReview} stud{adoptReview === 1 ? "y" : "ies"} adopted — legacy schedules still running
              </span>
              <Badge variant="warning">Review needed</Badge>
            </div>
            <p className="text-[12.5px] text-slate-600">
              Review this topic's competitors, then enable tracking to switch runs over.
            </p>
            <Link to="?tab=competitors" className="inline-block text-[12.5px] font-medium text-brand-600 hover:underline">
              Review competitors →
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Master toggle */}
      <Card className="bg-brand-50/40">
        <CardContent className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[13.5px] font-bold text-brand-950">Tracking</div>
            <p className="text-[12px] text-slate-500">Run this topic's prompts on a schedule and record metrics.</p>
          </div>
          <Toggle checked={enabled} onChange={setEnabled} label="Enable tracking" />
        </CardContent>
      </Card>

      {/* Lens cards */}
      <div className={cn("grid gap-4", isPulse ? "" : "sm:grid-cols-2")}>
        <Card>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <LensPill lens="field" />
              <span className="text-[13.5px] font-semibold text-brand-950 flex-1">Field sentiment tracking</span>
              <Toggle checked={fieldEnabled} onChange={setFieldEnabled} label="Enable field tracking" />
            </div>
            <p className="text-[12px] text-slate-500">{fieldPromptCount} prompt{fieldPromptCount === 1 ? "" : "s"} · asks about your brand directly.</p>
            <StatusPill on={fieldEnabled && enabled} />
          </CardContent>
        </Card>
        {!isPulse && (
          <Card>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                <LensPill lens="blind" />
                <span className="text-[13.5px] font-semibold text-brand-950 flex-1">Blind visibility tracking</span>
                <Toggle checked={blindEnabled} onChange={setBlindEnabled} label="Enable blind tracking" />
              </div>
              <p className="text-[12px] text-slate-500">{blindPromptCount} prompt{blindPromptCount === 1 ? "" : "s"} · neutral queries, no brands named.</p>
              <StatusPill on={blindEnabled && enabled} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Schedule */}
      <Card>
        <CardContent className="space-y-4">
          <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Schedule</span>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="track-cadence">Cadence</Label>
              <NativeSelect id="track-cadence" value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)}>
                {CADENCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </NativeSelect>
            </div>
            {cadence === "weekly" && (
              <div className="space-y-1.5">
                <Label htmlFor="track-day">Run day</Label>
                <NativeSelect id="track-day" value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value))}>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </NativeSelect>
              </div>
            )}
            {cadence === "monthly" && (
              <div className="space-y-1.5">
                <Label htmlFor="track-dom">Day of month</Label>
                <NativeSelect id="track-dom" value={dayOfMonth} onChange={(e) => setDayOfMonth(parseInt(e.target.value))}>
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
                </NativeSelect>
              </div>
            )}
            {cadence === "times_per_day" && (
              <div className="space-y-1.5">
                <Label htmlFor="track-tpd">Runs per day</Label>
                <NativeSelect id="track-tpd" value={timesPerDay} onChange={(e) => setTimesPerDay(parseInt(e.target.value))}>
                  {Array.from({ length: 23 }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{formatTimesPerDay(n)}</option>)}
                </NativeSelect>
              </div>
            )}
            {cadence !== "times_per_day" && (
              <div className="space-y-1.5">
                <Label htmlFor="track-hour">Run hour (UTC)</Label>
                <NativeSelect id="track-hour" value={hourUtc} onChange={(e) => setHourUtc(parseInt(e.target.value))}>
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
                </NativeSelect>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Models</Label>
            <ModelSelector selected={models} onChange={setModels} />
          </div>
        </CardContent>
      </Card>

      {/* Estimate + save */}
      {estimate && (
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 rounded-lg bg-brand-50 px-4 py-3 text-[12.5px] text-slate-600">
          <span>Estimated cost:</span>
          <b className="text-[13px] font-bold text-brand-600 tabular-nums">~{estimate.estimated_calls_per_month} AI calls / month</b>
          <span className="text-slate-400">
            {estimate.field_prompt_count + estimate.blind_prompt_count} prompt{estimate.field_prompt_count + estimate.blind_prompt_count === 1 ? "" : "s"} ×{" "}
            {estimate.brand_count} brand{estimate.brand_count === 1 ? "" : "s"} × {estimate.models.length} model{estimate.models.length === 1 ? "" : "s"} — shown before anything runs.
          </span>
        </div>
      )}
      {error && <p className="text-[12.5px] text-red-600">{error}</p>}
      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || (enabled && models.size === 0)}>
          {saving ? "Saving…" : "Save tracking"}
        </Button>
      </div>

      {/* Active studies */}
      {(resolved.activeStudies.field.length > 0 || resolved.activeStudies.blind.length > 0) && (
        <Card>
          <CardContent className="space-y-3">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Active studies</span>
            {resolved.activeStudies.field.length > 0 && <StudyList label="Field" studies={resolved.activeStudies.field} />}
            {resolved.activeStudies.blind.length > 0 && <StudyList label="Blind" studies={resolved.activeStudies.blind} />}
          </CardContent>
        </Card>
      )}

      {/* Adoptable studies */}
      {resolved.adoptableStudies.length > 0 && (
        <AdoptableStudies studies={resolved.adoptableStudies} topicId={topicId} onAdoptAll={adoptAll} onMutated={onMutated} />
      )}
    </div>
  );
}

function AdoptableStudies({ studies, topicId, onAdoptAll, onMutated }: { studies: TopicStudyRef[]; topicId: number; onAdoptAll: () => Promise<void>; onMutated: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const adoptAll = async () => {
    setBusy(true);
    setError("");
    try {
      await onAdoptAll();
    } catch (e: any) {
      setError(e?.message || "Could not adopt studies.");
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Adoptable studies</span>
        <p className="text-[12px] text-slate-500">Scheduled studies already running on this topic's prompts — adopt one to fold it into this topic's tracking.</p>
        <div className="flex items-center gap-3">
          <Button size="sm" disabled={busy} onClick={adoptAll}>
            {busy ? "Adopting…" : `Adopt all ${studies.length} studies`}
          </Button>
          <p className="text-[12px] text-slate-500">Adopts every scheduled study into this topic and suggests its competitor field. Nothing changes until you review and enable tracking — legacy schedules keep running as-is.</p>
        </div>
        {error && <p className="text-[12.5px] text-red-600">{error}</p>}
        {studies.map((s) => (
          <AdoptRow key={s.id} study={s} topicId={topicId} onMutated={onMutated} />
        ))}
      </CardContent>
    </Card>
  );
}

function StudyList({ label, studies }: { label: string; studies: TopicStudyRef[] }) {
  return (
    <div className="space-y-1">
      <div className="text-[11.5px] font-medium text-slate-400">{label}</div>
      {studies.map((s) => (
        <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
          <FlaskConical className="w-3.5 h-3.5 text-slate-300 shrink-0" />
          <span className="text-[13px] text-slate-700 truncate">{s.name}</span>
          <Link to={`/app/studies/${s.id}`} className="ml-auto text-[12px] font-medium text-brand-600 hover:underline shrink-0">Runs →</Link>
        </div>
      ))}
    </div>
  );
}

function AdoptRow({ study, topicId, onMutated }: { study: TopicStudyRef; topicId: number; onMutated: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const adopt = async () => {
    setBusy(true);
    setError("");
    setNote("");
    try {
      const res = await api.topics.adoptStudy(topicId, study.id);
      // The server skips the compile while tracking is off (review gate) or
      // while other adoptable studies remain (duplicated tracking) — say which.
      if (res.applied === false) {
        setNote(res.review_required
          ? "Adopted — review competitors, then enable tracking"
          : `Adopted — ${res.remaining} more to adopt before tracking compiles`);
      }
      await onMutated();
    } catch (e: any) {
      setError(e?.message || "Could not adopt.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <FlaskConical className="w-3.5 h-3.5 text-slate-300 shrink-0" />
      <span className="text-[13px] text-slate-700 truncate">{study.name}</span>
      {note && <span className="text-[11.5px] text-slate-500">{note}</span>}
      {error && <span className="text-[11.5px] text-red-600">{error}</span>}
      <Button variant="secondary" size="sm" className="ml-auto shrink-0" disabled={busy} onClick={adopt}>
        {busy ? "Adopting…" : "Adopt"}
      </Button>
    </div>
  );
}

/** Enabled/Off status pill matching the prototype's lens-state chip. */
function StatusPill({ on }: { on: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold",
        on ? "bg-green-50 text-green-600" : "bg-slate-100 text-slate-500",
      )}
    >
      {on ? "Enabled" : "Off"}
    </span>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
        checked ? "bg-brand-600" : "bg-slate-200",
      )}
    >
      <span className={cn("inline-block h-5 w-5 transform rounded-full bg-white transition-transform", checked ? "translate-x-5" : "translate-x-0.5")} />
    </button>
  );
}
